import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@mui/material";

import { useGetTaxZones } from "../../../hooks/tax/useGetTaxZones";
import { useDeleteTaxZone } from "../../../hooks/tax/useDeleteTaxZone";
import { useUpdateTaxZone } from "../../../hooks/tax/useUpdateTaxZone";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { TaxZonesTableColumns } from "./Table/TaxZonesTableColumns";
import TaxZonesTableRow from "./Table/TaxZonesTableRow";
import FormAddEditTaxZone from "./FormAddEditTaxZone";

const TaxZonesTable = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || "en").startsWith("ar");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteTaxZoneMutate, isDeleting } = useDeleteTaxZone();
    const { updateTaxZoneMutate } = useUpdateTaxZone();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleMenuDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/tax",
        deleteSingle: deleteTaxZoneMutate,
    });

    const params = useMemo(() => ({
        page,
        per_page: perPage,
        search: search || undefined,
        is_active: statusFilter === "" ? undefined : statusFilter,
        sort_by: "created_at",
        sort_order: order,
    }), [page, perPage, search, order, statusFilter]);

    const { zones, meta, isLoading, error, refetch } = useGetTaxZones(params);

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ];

    const openCreate = () => {
        setEditingItem(null);
        setDialogOpen(true);
    };

    const openEdit = () => {
        if (!selectedItem) return;
        setEditingItem(selectedItem);
        setDialogOpen(true);
        handleMenuClose();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingItem(null);
    };

    const handleToggleActive = (item) => {
        updateTaxZoneMutate({
            id: item.id,
            data: { is_active: !item.is_active },
        });
    };

    const renderRow = (rowParams) => (
        <TaxZonesTableRow
            key={rowParams.item.id}
            item={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            onToggleActive={handleToggleActive}
        />
    );

    return (
        <>
            <TableLayout
                title={isArabic ? "المناطق الضريبية" : "Tax Zones"}
                subtitle={isArabic ? "إدارة المناطق الضريبية الجغرافية" : "Manage geographic tax zones"}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? "إضافة منطقة" : "Add Zone"}
                onAddClick={openCreate}
                isLoading={isLoading}
                error={error}
                items={zones}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                order={order}
                onRequestSort={handleRequestSort}
                columns={TaxZonesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={openEdit}
                onMenuDelete={handleMenuDelete}
                isDeletingSingle={isDeleting}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, zones)}
                onRowCheckboxClick={handleRowCheckboxClick}
                bulkDeleteDisabled
                filters={tableFilters}
                onClearAllFilters={() => setStatusFilter("")}
                emptyLabelAr="لا توجد مناطق ضريبية"
                emptyLabelEn="No tax zones"
                refetch={refetch}
            />

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
                <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
                    <FormAddEditTaxZone
                        isEdit={!!editingItem}
                        currentData={editingItem}
                        onSubmitSuccess={closeDialog}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TaxZonesTable;
